/**
 * `identification` slice mutations (ENG-4832 / ENG-4925 / ENG-4833).
 *
 * Records how the camera being added was identified (detected by discovery,
 * searched, typed into "My camera isn't listed", …) and owns the open state of
 * the not-listed dialogs (C1/C2) and the "Basic controls" notice (D1).
 *
 * Same shape as `failed-devices.slice.ts`: pure helpers over an immer
 * `Draft<State>`, composed by callers through the store's `produce`
 * (`producer((draft) => openNotListed(draft, …))`).
 *
 * Per rule: think-in-systems (ai.knowledge/global) — the identification answer
 * is one primitive read by three consumers (save events, StudioDevice
 * persistence, the D1 trigger), so it lives in one slice instead of being
 * re-derived at each.
 */
import type {
  IdentifiedBy,
  NotListedOpenedFrom
} from '#~/analytics/domains/device-identification.js';
import type { Draft, IdentificationState, NotListedStep, State } from './types.js';

/** Initial (and post-reset) slice value. A fresh object per call so drafts never share it. */
export const createIdentificationState = (): IdentificationState => ({
  identifiedBy: undefined,
  typedBrand: undefined,
  typedModel: undefined,
  detectedBrand: undefined,
  detectedModel: undefined,
  notListed: { open: false, step: 'c1', openedFrom: 'list' },
  basicControlsShown: false,
  basicControlsOpen: false
});

/**
 * The `identifiedBy` the not-listed flow's Continue records, by entry point
 * (ENG-4832 / ENG-4924): the list's "My camera isn't listed" is `not_listed`;
 * a not-yet-supported model — picked from the list or recognised by discovery —
 * is `not_yet_supported_picked` (ENG-4924 names only the pick; the task extends
 * it to the recognised case, which also saves on the generic driver).
 *
 * Per rule: think-in-systems (ai.knowledge/global) — one decision here, so no
 * caller (the add wizard today, the RTSP step tomorrow) re-derives it.
 */
export const notListedIdentifiedBy = (openedFrom: NotListedOpenedFrom): IdentifiedBy =>
  openedFrom === 'list' ? 'not_listed' : 'not_yet_supported_picked';

/**
 * Answers written by the not-listed flow's Continue (C1 or C2): C3 shows the
 * typed text read-only and its Change reopens the flow (ENG-4832 / ENG-4924).
 */
export const isNotListedAnswer = (identifiedBy: IdentifiedBy | undefined): boolean =>
  identifiedBy === 'not_listed' || identifiedBy === 'not_yet_supported_picked';

/** Seed text for C1: what the user had typed into a picker when they chose "isn't listed". */
export interface NotListedSeed {
  brand?: string | undefined;
  model?: string | undefined;
}

/** Trimmed seed text, or `undefined` when there is nothing to carry over. */
const toSeed = (text: string | undefined): string | undefined => text?.trim() || undefined;

/**
 * Open the "My camera isn't listed" flow (ENG-4832).
 *
 * `step` is the entry screen: C1 from the list (default), C2 for a
 * not-yet-supported camera. `openedFrom` names the entry point (ENG-4832
 * `opened_from`); it defaults to `list` so the list's callers stay unchanged,
 * and it decides the answer Continue records (`notListedIdentifiedBy`).
 *
 * Typed values are kept only when the flow was already continued in this add
 * (`isNotListedAnswer`), so reopening from C3 shows what the user typed; any
 * other entry starts from empty fields.
 *
 * ENG-4831 B5 / ENG-4924: `typed` carries the text the user already typed into
 * the Brand / Model picker (and the brand they had selected) into C1, or the
 * picked / detected names into C2, so they never retype it. A non-empty seed
 * also wins when reopening from C3 — the user explicitly typed something new —
 * while an absent seed keeps the earlier typed value.
 */
export const openNotListed = (
  draft: Draft,
  step: NotListedStep = 'c1',
  typed?: NotListedSeed,
  openedFrom: NotListedOpenedFrom = 'list'
): void => {
  const slice = draft.identification;

  if (!isNotListedAnswer(slice.identifiedBy)) {
    slice.typedBrand = undefined;
    slice.typedModel = undefined;
  }

  const brand = toSeed(typed?.brand);
  const model = toSeed(typed?.model);

  if (brand) slice.typedBrand = brand;
  if (model) slice.typedModel = model;

  slice.notListed = { open: true, step, openedFrom };
};

/**
 * C3's Change: reopen the flow on the screen (and entry point) that produced
 * the current answer, with the typed text kept — C1 after "isn't listed", C2
 * after a not-yet-supported pick (ENG-4832 / ENG-4924).
 */
export const reopenNotListed = (draft: Draft): void => {
  const { step, openedFrom } = draft.identification.notListed;

  openNotListed(draft, step, undefined, openedFrom);
};

/** Close the not-listed flow without touching the identification answer. */
export const closeNotListed = (draft: Draft): void => {
  draft.identification.notListed.open = false;
};

/** Record how the saved model was arrived at. */
export const markIdentifiedBy = (draft: Draft, identifiedBy: IdentifiedBy): void => {
  draft.identification.identifiedBy = identifiedBy;
};

/** Display names discovery resolved for the selected row ('' / undefined = none). */
export const setDetectedCamera = (
  draft: Draft,
  detected: { brand?: string | undefined; model?: string | undefined }
): void => {
  draft.identification.detectedBrand = detected.brand;
  draft.identification.detectedModel = detected.model;
};

/** Free text from C1. Stored trimmed: whitespace-only input means "not typed". */
export const setTypedCamera = (draft: Draft, typed: { brand: string; model: string }): void => {
  draft.identification.typedBrand = typed.brand.trim();
  draft.identification.typedModel = typed.model.trim();
};

/** Forget typed values, e.g. after "Did you mean" picked a listed model instead. */
export const clearTypedCamera = (draft: Draft): void => {
  draft.identification.typedBrand = undefined;
  draft.identification.typedModel = undefined;
};

/** Show D1 and remember it was shown, so it appears at most once per add. */
export const openBasicControls = (draft: Draft): void => {
  draft.identification.basicControlsOpen = true;
  draft.identification.basicControlsShown = true;
};

/** Hide D1 (Continue or the header X). `basicControlsShown` stays set for the rest of the add. */
export const closeBasicControls = (draft: Draft): void => {
  draft.identification.basicControlsOpen = false;
};

/** Back to the initial state; the wizard calls this when it closes. */
export const resetIdentification = (draft: Draft): void => {
  draft.identification = createIdentificationState();
};

/**
 * Should the "Basic controls" notice (D1) show when the user leaves the
 * brand/model step?
 *
 * Yes when the camera will run on the generic driver, however it was chosen
 * (Board D, Travis 2026-10-23): the resolved template's model is `generic`
 * (a brand's "Other" pick or a brand-only detection). Never after the
 * "isn't listed" / "isn't supported yet" pop-ups (ENG-4925): `not_listed` and
 * `not_yet_supported_picked` are only ever written by their Continue, which
 * already explained the driver, and D1 names a camera they could not. At most
 * once per add. Never on multi-add, whose rows are committed without the
 * brand/model step: it lists its generic-driver cameras in one dialog before
 * the save instead (identification/bulk.ts, ENG-4942).
 */
export const shouldShowBasicControls = (
  state: Pick<State, 'identification'> & {
    source: Pick<State['source'], 'bulkMode'>;
    manufacturer: Pick<State['manufacturer'], 'config'>;
  }
): boolean => {
  const { identification } = state;

  // Absent slice (fixtures predating it) or a multi-add batch: never.
  if (!identification || state.source.bulkMode) {
    return false;
  }

  if (identification.basicControlsShown) {
    return false;
  }

  // The C1/C2 pop-ups already told the user about the driver (ENG-4925).
  if (isNotListedAnswer(identification.identifiedBy)) {
    return false;
  }

  return state.manufacturer.config?.model?.generic === true;
};
